import {
  Container,
  Grid,
  IconButton,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import { EmptyStateIcon, Filter } from "assets/Icon";
import {
  getInvestmentPortfolioRequestHistoryForm,
  getInvestmentPortfolioRequestHistoryList,
} from "store/slices/investment/investmentSlice";
import { useEffect, useState } from "react";

import EmptyState from "components/EmptyState";
import { INVESTMENT_HISTORY_HEAD } from "./constants";
import InvestmentRequestFilters from "./InvestmentRequestFilters";
import NumberFormats from "components/NumberFormats";
import Pagination from "components/Pagination";
import Table from "components/Table";
import TableSkeleton from "components/Skeletons/Table.skeleton";
import WrapperAmount from "pages/Wallet/Component/TableSpotHistory/Component/WrapperAmount";
import useDispatch from "hooks/useDispatch";
import useIsMobileView from "hooks/useIsMobileView";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import styled from "@emotion/styled";
import { formatDateToJalali } from "utils/helpers/time";
import { ROUTE_PATHS } from "routes/paths";
import InvestmentRequestHistoryBox from "./components/HistoryBox";

function InvestmentRequest() {
  const isMobileView = useIsMobileView();
  const dispatch = useDispatch();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const investmentHistoryList = useSelector(
    (state) => state.investment.investmentHistoryList
  );

  const [searchParams] = useSearchParams();

  const page = +searchParams.get("page") || 1;
  const perPage = +searchParams.get("perPage") || 10;
  const portfolioId = +searchParams.get("portfolioId") || -1;
  const startDate = searchParams.get("startDate") || "";
  const endDate = searchParams.get("endDate") || "";
  const coinId = +searchParams.get("coinId") || -1;
  const sideId = +searchParams.get("sideId") || 0;

  const [showFilter, setShowFilter] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChangeTab = (_, newValue) => {
    navigate(newValue);
  };

  const handleGetInvestmentPortfolioRequestHistoryList = async ({
    page,
    perPage,
  }) => {
    setLoading(true);
    await dispatch(
      getInvestmentPortfolioRequestHistoryList({
        portfolioId,
        startDate: formatDateToJalali(startDate),
        endDate: formatDateToJalali(endDate),
        coinId,
        sideId,
        pageSize: perPage,
        pageNumber: page,
      })
    );
    setLoading(false);
  };

  const handleChangePaginate = ({ page, perPage }) => {
    handleGetInvestmentPortfolioRequestHistoryList({ page, perPage });
  };

  useEffect(() => {
    handleGetInvestmentPortfolioRequestHistoryList({ page, perPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioId, startDate, endDate, coinId, sideId]);

  useEffect(() => {
    dispatch(getInvestmentPortfolioRequestHistoryForm());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formattedItems = investmentHistoryList?.data?.map((item) => ({
    ...item,
    coin: <WrapperAmount data={item} />,
    portfolioOwner: item?.portfolioOwner,
    fee: (
      <Typography color="text.main" fontWeight={500} variant="h6">
        <NumberFormats value={item?.fee} />{" "}
        <Typography
          component="span"
          color="text.secondary"
          fontWeight={400}
          variant="subtitle1"
        >
          {item?.coinName}
        </Typography>
      </Typography>
    ),
    side: item?.side,
    state: item?.state,
    amount: (
      <Typography color="text.main" fontWeight={500} variant="h6">
        <NumberFormats value={item?.amount} />{" "}
        <Typography
          component="span"
          color="text.secondary"
          fontWeight={400}
          variant="subtitle1"
        >
          {item?.coinName}
        </Typography>
      </Typography>
    ),
    date: (
      <>
        {item?.date}/{item?.time}
      </>
    ),
    dateTimeEdit: (
      <>
        {item?.dateEdit}/{item?.timeEdit}
      </>
    ),
    comment: item?.comment,
  }));

  return (
    <Container maxWidth="100%">
      <Grid container alignItems="center">
        {isMobileView && (
          <Grid item xs={12}>
            <TabsStyle value={location.pathname} onChange={handleChangeTab}>
              <Tab
                label=" Request History"
                value={ROUTE_PATHS.history.request}
              />
              <Tab
                label="Investment History"
                value={ROUTE_PATHS.history.investment}
              />
            </TabsStyle>
          </Grid>
        )}

        <Grid item xs={6}>
          <Grid container>
            <Typography color="text.main" variant="h4">Request History</Typography>
          </Grid>
        </Grid>

        <Grid item xs={6} textAlign="right">
          <IconButton
            onClick={() => {
              setShowFilter((v) => !v);
            }}
            aria-label="filter"
          >
            <Filter color={theme.palette.text.main} />
          </IconButton>
        </Grid>
      </Grid>

      {showFilter && <InvestmentRequestFilters />}

      <Grid container mt={2} spacing={2}>
        <>
          {isMobileView ? (
            <>
              {investmentHistoryList?.data?.length ? (
                <>
                  {investmentHistoryList?.data?.map((investment) => (
                    <Grid item xs={12} sm={6}>
                      <InvestmentRequestHistoryBox item={investment} />
                    </Grid>
                  ))}
                </>
              ) : (
                <Grid item xs={12}>
                  <EmptyState
                    text="No History"
                    icon={
                      <EmptyStateIcon color={theme.palette.text.secondary} />
                    }
                  />
                </Grid>
              )}
            </>
          ) : (
            <>
              {!loading ? (
                <Grid item xs={12}>
                  <Table
                    headers={INVESTMENT_HISTORY_HEAD}
                    items={formattedItems}
                  />
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <TableSkeleton />
                </Grid>
              )}
            </>
          )}
        </>

        <Grid item xs={12}>
          <Pagination
            perPage={investmentHistoryList?.pageSize}
            count={investmentHistoryList?.totalPages}
            onChange={handleChangePaginate}
          />
        </Grid>
      </Grid>
    </Container>
  );
}

export default InvestmentRequest;
const TabsStyle = styled(Tabs)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.modules.border}`,
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(3),
  width: "100%",

  ".MuiTabs-flexContainer": {
    justifyContent: "space-around",
  },
}));
